<template>
  <div class="container">
    <div class="gutter">
      <div class="columns is-centered mb-0">
        <div class="column is-10 pt-0">
          <onboarding-wizard-header
            active-step="finalize"
            :plan-clickable="true"
            @plan-clicked="handleChangePlanClick"
          />

          <h1 class="title is-1">
            {{ pageHeading }}
          </h1>

          <div
            v-if="loadingInvoice"
            class="columns"
          >
            <div class="column is-12 has-text-centered">
              <br>
              <span
                class="loader xl"
                style="display: inline-block; width: 3em; height: 3em;"
              />
            </div>
          </div>

          <div
            v-if="!loadingInvoice && stripeInvoice && !checkoutComplete"
          >
            <div class="columns is-mobile is-vcentered">
              <div class="column is-narrow">
                <span
                  class="icon-static xl margin-bottom-sm "
                  :class="selectedPlan.slug"
                />
              </div>
              <div class="column">
                <h3 class="title-lowercase is-5">
                  {{ selectedPlan.product_name }}
                </h3>
                <p>{{ selectedPlan.amount|formattedDbAmount(null, 2, currencyCode) }} / {{ selectedPlan.interval }} {{ lineItemDescriptionExtra }}</p>
              </div>
              <div class="column is-narrow has-text-right">
                <b-tooltip
                  label="Change your plan"
                  type="is-dark"
                  position="is-left"
                >
                  <a
                    class="icon-button trash"
                    @click="handleChangePlanClick"
                  />
                </b-tooltip>
              </div>
            </div>

            <p
              v-if="trialMode"
              class="small"
            >
              Your free trial will run for 30 days. Cancel anytime <em>before</em> {{ trialEndDate|formattedDate }} to avoid being charged.
            </p>

            <table class="table is-fullwidth">
              <tr v-if="stripeInvoice.total_discount_amounts && stripeInvoice.total_discount_amounts.length > 0">
                <td>
                  <h3 class="title is-3 is-pulled-left">
                    Subtotal
                  </h3>
                </td>
                <td />
                <td>
                  <span
                    class="is-pulled-right text caption"
                  >{{ stripeInvoice.subtotal|formattedDbAmount(null, 2, currencyCode) }}</span>
                </td>
              </tr>
              <tr v-if="stripeInvoice.total_discount_amounts && stripeInvoice.total_discount_amounts.length > 0">
                <td />
                <td />
                <td>
                  <span
                    class="is-pulled-right text caption"
                  >{{ stripeInvoice.discount.coupon.name }}&nbsp;&nbsp;-{{ stripeInvoice.total_discount_amounts[0].amount|formattedDbAmount(null, 2, currencyCode) }}</span>
                </td>
              </tr>
              <tr v-if="!trialMode">
                <td>
                  <h3 class="title is-3 is-pulled-left">
                    Amount due<span v-if="trialMode"> on {{ trialEndDate|formattedDate }}</span>
                  </h3>
                </td>
                <td />
                <td>
                  <span class="is-pulled-right text caption">{{ amountDue|formattedDbAmount(null, 2, currencyCode) }}</span>
                </td>
              </tr>
            </table>
          </div>

          <br>

          <form class="form">
            <div class="columns">
              <div class="column is-half mt-4">
                <h3 class="title is-3">
                  Card details
                </h3>

                <div class="field">
                  <div class="control">
                    <input
                      v-model="billing_details.name"
                      class="input"
                      type="text"
                      required
                      placeholder="Cardholder name"
                    >
                  </div>
                </div>

                <div class="field">
                  <div :id="cardElId" />

                  <p
                    v-if="cardError"
                    id="card-errors"
                    class="help is-danger"
                    role="alert"
                  >
                    {{ cardError }}
                  </p>
                </div>

                <p class="is-pulled-left mr-1 mb-2">
                  Secure checkout powered by
                  <a
                    style="text-decoration: underline;"
                    href="https://stripe.com/"
                    target="_blank"
                  >Stripe</a>
                </p>

                <figure class="is-pulled-left mb-5">
                  <img
                    src="@/assets/card-brands.png"
                    alt="Accepted card brands: Visa, Mastercard, American Express, Maestro."
                  >
                </figure>

                <div class="is-clearfix" />

                <div class="field is-horizontal">
                  <div class="field is-grouped">
                    <p class="control is-expanded">
                      <input
                        v-model="promoCode"
                        :disabled="promoInputDisabled"
                        class="input"
                        type="text"
                        placeholder="Promo code"
                      >
                    </p>
                    <p class="control">
                      <button
                        class="button is-primary is-large"
                        :disabled="promoButtonDisabled"
                        @click.prevent="handleApplyClick"
                      >
                        {{ promoButtonLabel }}
                      </button>
                    </p>
                  </div>
                </div>
                <p
                  v-if="promoError"
                  class="help is-danger"
                  role="alert"
                >
                  {{ promoError }}
                </p>
              </div> <!-- .column (card details) -->

              <div class="column is-half mt-4">
                <h3 class="title is-3">
                  Invoice details
                </h3>

                <p>
                  Need an invoice?
                  <span
                    style="text-decoration: underline; cursor: pointer"
                    @click="showInvoiceDetails = !showInvoiceDetails"
                  >Fill in your company details.</span>
                </p>
                <br>

                <div
                  v-if="showInvoiceDetails"
                  class="invoice-details"
                >
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">Company</label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input
                            id="id_billing_company_name"
                            v-model="teamData.billing_company_name"
                            type="text"
                            name="billing_company_name"
                            class="input"
                          >
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">Contact</label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input
                            id="id_billing_contact"
                            v-model="teamData.billing_contact"
                            type="text"
                            name="billing_contact"
                            class="input"
                          >
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">Address 1</label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input
                            id="id_address_1"
                            v-model="teamData.address_1"
                            type="text"
                            name="address_1"
                            class="input"
                          >
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">Address 2</label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input
                            id="id_address_2"
                            v-model="teamData.address_2"
                            type="text"
                            name="address_2"
                            class="input"
                          >
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">City</label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input
                            id="id_city"
                            v-model="teamData.city"
                            type="text"
                            name="city"
                            class="input"
                          >
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">County/area</label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input
                            id="id_county"
                            v-model="teamData.county"
                            type="text"
                            name="county"
                            class="input"
                          >
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">Postal code</label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <input
                            id="id_postal_code"
                            v-model="teamData.postal_code"
                            type="text"
                            name="postal_code"
                            class="input"
                          >
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="field is-horizontal">
                    <div class="field-label is-normal">
                      <label class="label">Country</label>
                    </div>
                    <div class="field-body">
                      <div class="field">
                        <div class="control">
                          <div class="select">
                            <select
                              v-model="teamData.country"
                            >
                              <option :value="''">
                                --------
                              </option>
                              <option
                                v-for="option in countryOptions"
                                :key="'checkout-country-' + option.value"
                                :value="option.value"
                              >
                                {{ option.display_name }}
                              </option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div> <!-- .columns -->
      <div class="columns is-centered mt-4 mb-6">
        <div class="column is-10">
          <div class="field is-horizontal">
            <button
              class="button is-large is-accent"
              :class="{ 'is-loading': busy }"
              :disabled="!canSubmit || busy"
              @click.prevent="handlePayClick"
            >
              {{ submitButtonText }}
            </button>
          </div>
        </div>
      </div> <!-- .columns (button) -->
    </div><!-- /div .gutter -->
  </div><!-- /div .container -->
</template>

<script>
import OnboardingWizardHeader from '@/components/OnboardingWizardHeader.vue'
import subscriptionClient from '@/api/subscriptions.js'
import teamClient from '@/api/teams.js'
import { formatComponentName } from '@/utils/common.js'
import { logSubscriptionPurchase, logSubscriptionTrialPurchase } from '@/utils/segment.js'

export default {
  name: 'SubscriptionCheckout',

  components: {
    OnboardingWizardHeader
  },

  data () {
    return {
      billing_details: {
        name: ''
      },
      promoCode: '',
      promoApplied: false,
      promoError: null,
      teamData: {
        name: '', // Don't change the name in this component. Needed for API though.
        billing_company_name: '',
        billing_contact: '',
        address_1: '',
        address_2: '',
        city: '',
        county: '',
        postal_code: '',
        country: ''
      },
      countryOptions: [],
      clientSecret: null,
      cardElId: 'card-element',
      cardElSelector: '#card-element',
      cardComplete: false,
      cardError: null,
      stripe: null,
      stripeElements: null,
      stripeCard: null,
      loadingInvoice: true,
      stripeInvoice: null,
      showInvoiceDetails: false,
      busy: false,
      checkoutComplete: false
    }
  },

  computed: {
    canSubmit: function () {
      return this.cardComplete && this.billing_details.name && this.$store.getters['subscriptions/selectedPlan']
    },

    selectedPlan: function () {
      return this.$store.getters['subscriptions/selectedPlan']
    },

    currencyCode: function () {
      return this.$store.getters['subscriptions/selectedPlan'].currency
    },

    componentName: function () {
      return formatComponentName(this)
    },

    promoInputDisabled: function () {
      return this.promoApplied
    },

    promoButtonDisabled: function () {
      const enabled = this.promoApplied || this.promoCode
      return !enabled
    },

    promoButtonLabel: function () {
      if (this.promoApplied) {
        return 'Clear'
      }
      return 'Apply'
    },

    pageHeading: function () {
      if (this.trialMode) {
        return 'Start 30-day free trial'
      }
      return 'Checkout'
    },

    lineItemDescriptionExtra: function () {
      if (this.trialMode) {
        return ' after trial ends. Cancel anytime.'
      }
      return ''
    },

    trialEndDate: function () {
      if (this.stripeInvoice) {
        return new Date(this.stripeInvoice.lines.data[0].period.end * 1000)
      }
      return ''
    },

    amountDue: function () {
      if (this.stripeInvoice) {
        if (this.trialMode) {
          return this.stripeInvoice.lines.data[0].plan.amount
        }
        return this.stripeInvoice.amount_due
      }
      return 0
    },

    submitButtonText: function () {
      if (this.trialMode) {
        return 'Start Free Trial'
      }
      return 'Confirm & Pay'
    },

    trialMode: function () {
      return this.$store.getters['subscriptions/createSubscriptionWithTrial']
    }
  },

  mounted () {
    this.stripe = window.Stripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY)
    this.stripeElements = this.stripe.elements()

    // Custom styling can be passed to options when creating an Element.
    // (Note that this demo uses a wider set of styles than the guide below.)
    const style = {
      base: {
        color: '#32325d',
        fontFamily: '"Euclid Circular", Avenir, sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '16px',
        '::placeholder': {
          color: '#b5b5b5'
        }
      },
      invalid: {
        color: '#fa755a',
        iconColor: '#fa755a'
      }
    }

    // Create an instance of the card Element.
    this.stripeCard = this.stripeElements.create('card', { style })
    this.stripeCard.on('change', (event) => {
      this.cardComplete = event.complete
    })
    this.stripeCard.mount(this.cardElSelector)

    // No way to guarantee we have the team data without loading it first.
    this.$store.dispatch('user/setMe').then(() => {
      teamClient.options()
        .then((response) => {
          this.countryOptions = response.data.actions.POST.country.choices
          this.loadTeamData()
        })
    })

    this.fetchUpcomingInvoice()
  },

  methods: {
    loadTeamData: function () {
      // Load default invoice data from team profile.
      const team = Object.assign({}, this.$store.getters['user/currentTeam'])

      // Copy logged in team data to local `teamData`.
      const teamKeys = Object.keys(this.teamData)
      for (const [key, value] of Object.entries(team)) {
        if (teamKeys.includes(key)) {
          this.teamData[key] = value
        }
      }
    },

    handleChangePlanClick: function () {
      this.$store.dispatch('subscriptions/selectPlan', null)
      this.$router.push({ name: 'pricing' })
    },

    getOrCreateSubscription: function (params) {
      let subscription = this.$store.getters['subscriptions/inProgressSubscription']

      if (subscription) {
        return new Promise(function (resolve, reject) {
          resolve(subscription)
        })
      }

      return subscriptionClient.create(params)
        .then((subscriptionResponse) => {
          subscription = subscriptionResponse.data
          this.$store.dispatch('subscriptions/setInProgressSubscription', subscription)
          return subscription
        })
    },

    handlePayClick: function () {
      const vm = this
      const user = this.$store.getters['user/currentUser']
      let subscription = null

      vm.busy = true

      // Store core subscription details for analytics.
      const revenue = vm.stripeInvoice.amount_due / 100.0
      const currency = vm.stripeInvoice.currency
      const planName = vm.$store.getters['subscriptions/selectedPlan'].product_name

      teamClient
        .partialUpdate({ team: this.teamData, user })
        .then(() => {
          vm.stripe.createPaymentMethod({
            type: 'card',
            card: vm.stripeCard,
            billing_details: vm.billing_details
          })
            .then((createPaymentMethodResponse) => {
              if (createPaymentMethodResponse.hasOwnProperty('error')) {
                throw createPaymentMethodResponse
              }

              const paymentMethodId = createPaymentMethodResponse.paymentMethod.id
              const createParams = {
                payment_method_id: paymentMethodId,
                plan: vm.$store.getters['subscriptions/selectedPlan'].pk,
                promo_code: this.promoCode
              }
              if (vm.trialMode) {
                createParams.with_trial = true
              }
              this.getOrCreateSubscription(createParams)
                .then((sub) => {
                  subscription = sub

                  // We need to handle SCA payments that are incomplete.
                  if (subscription.status === 'incomplete') {
                    return vm.stripe.confirmCardPayment(subscription.payment_intent_client_secret, {
                      payment_method: paymentMethodId
                    })
                      .then((confirmResponse) => {
                        if (confirmResponse.hasOwnProperty('error')) {
                          throw confirmResponse
                        }

                        // Get the backend to update the subscription status.
                        return subscriptionClient.activate(subscription.pk, {
                          payment_intent: confirmResponse.paymentIntent.id
                        })
                      })
                  }

                  // We also need to handle trials where payment is taken later
                  // and we just "set up" the card for now.
                  if (subscription.setup_intent_client_secret) {
                    return vm.stripe.confirmCardSetup(subscription.setup_intent_client_secret, {
                      payment_method: paymentMethodId
                    })
                      .then((confirmResponse) => {
                        if (confirmResponse.hasOwnProperty('error')) {
                          throw confirmResponse
                        }
                      })
                  }
                })
                .then(() => {
                  if (this.trialMode) {
                    logSubscriptionTrialPurchase(planName, revenue, currency)
                  } else {
                    logSubscriptionPurchase(planName, revenue, currency)
                  }
                  // Reload user/team data which will get the subscription details.
                  vm.$store.dispatch('common/initAuthenticated')
                    .then(() => {
                      vm.checkoutComplete = true
                      vm.$store.dispatch('subscriptions/selectPlan', null)
                        .then(() => {
                          vm.$router.push({ name: 'subscription', params: { showCheckoutSuccess: true } })
                        })
                    })
                })
                .catch(error => {
                  // All-purpose error handler
                  vm.busy = false
                  if (error.hasOwnProperty('response')) { // Errors from backend API.
                    vm.cardError = error.response.data.error.message
                  }
                  if (error.hasOwnProperty('error')) { // Errors from client-side Stripe interactions.
                    vm.cardError = error.error.message
                  }
                })
            })
            .catch(error => {
              // Catch errors in setting up payment method (before linking to customer).
              vm.busy = false
              if (error.hasOwnProperty('error')) { // Errors from client-side Stripe interactions.
                vm.cardError = error.error.message
              }
            })
        })
    },

    fetchUpcomingInvoice: function () {
      this.loadingInvoice = true
      this.busy = true
      this.promoError = null

      const subscriptionParams = {
        plan: this.selectedPlan.pk,
        promo_code: this.promoCode
      }
      if (this.trialMode) {
        subscriptionParams.with_trial = true
      }
      return subscriptionClient.upcoming(subscriptionParams)
        .then((response) => {
          this.stripeInvoice = response.data
          this.loadingInvoice = false
          this.busy = false
          this.promoApplied = !!this.promoCode // If we're here and had a promo code it was applied!
        })
        .catch((error) => {
          this.loadingInvoice = false
          this.busy = false
          this.promoError = error.response.data.error.message
          this.promoApplied = false
        })
    },

    handleApplyClick: function () {
      if (this.promoApplied) {
        this.clearPromoCode()
      } else {
        this.applyPromoCode()
      }
    },

    applyPromoCode: function () {
      this.fetchUpcomingInvoice()
    },

    clearPromoCode: function () {
      this.promoCode = ''
      this.fetchUpcomingInvoice()
    }
  },

  metaInfo () {
    let title = 'Checkout'

    if (this.trialMode) {
      title = 'Free Trial Checkout'
    }

    return {
      title
    }
  }
}
</script>

<style scoped lang="scss">
.total {
  clear: both;
  overflow: hidden;
}

.title-lowercase {
  margin-bottom: 0px;
}

.StripeElement {
  box-sizing: border-box;
  height: 46px;
  padding: 13px;
  border-radius: 4px;
  background-color: #f5f6f6;

  box-shadow: inset 0px 1px 3px rgba(0, 0, 0, 0.1);
  -webkit-transition: box-shadow 150ms ease;
  transition: box-shadow 150ms ease;
}

.StripeElement--focus {
  box-shadow: 0 0 0 0.125em rgba(20, 33, 54, 0.25);
}

.StripeElement--invalid {
  border-color: #fa755a;
}

.StripeElement--webkit-autofill {
  background-color: #fefde5 !important;
}
</style>
